/**
 * Formato del archivo de respaldo y su validacion.
 *
 * Este modulo es CODIGO PURO: no importa Dexie ni toca la base de datos. Se
 * separo a proposito de backup.ts para poder probarlo sin navegador, porque es
 * la pieza que decide si los datos del dispositivo se reemplazan o no. Un error
 * aqui destruye informacion.
 */

import { calcularChecksum, checksumCoincide } from './checksum';
import { ErrorRespaldo } from '@/domain/errors';
import { esFechaLocal, minima, maxima, type FechaLocal } from '@/domain/dates';
import {
  TABLAS,
  ETIQUETAS_TABLA,
  VERSION_ESQUEMA,
  escribirTabla,
  leerTabla,
  type MapaTablas,
  type NombreTabla,
} from './types';

export const IDENTIFICADOR_APP = 'control-negocio-comida';

/**
 * Version del FORMATO del archivo, distinta de la version del esquema.
 * El formato describe la envoltura; el esquema, el contenido de las tablas.
 */
export const FORMATO_VERSION = 1;

export interface ArchivoRespaldo {
  app: string;
  formatVersion: number;
  schemaVersion: number;
  exportedAt: string;
  /** 'standalone' si se exporto desde la app instalada, 'navegador' si no. */
  device: string;
  counts: Record<string, number>;
  dateRange: { from: FechaLocal; to: FechaLocal } | null;
  checksum: string;
  data: MapaTablas;
}

export interface ResumenRespaldo {
  formatVersion: number;
  schemaVersion: number;
  exportedAt: string;
  device: string;
  conteos: Array<{ tabla: NombreTabla; etiqueta: string; registros: number }>;
  totalRegistros: number;
  rangoFechas: { desde: FechaLocal; hasta: FechaLocal } | null;
  checksumValido: boolean;
  requiereMigracion: boolean;
  advertencias: string[];
  /**
   * true = la importacion normal queda BLOQUEADA (Req. 3.6).
   *
   * Ocurre cuando el archivo trae suma de verificacion y no coincide: fue
   * modificado o se corrompio. La pantalla no ofrece el boton de importar.
   *
   * No es un rechazo absoluto a proposito: si ese archivo danado fuera el
   * unico respaldo existente, negarlo por completo garantizaria la perdida
   * total. Queda disponible una confirmacion reforzada aparte, para
   * recuperacion, que obliga a aceptar el riesgo de forma explicita.
   */
  bloqueaImportacion: boolean;
}

export interface RespaldoAnalizado {
  archivo: ArchivoRespaldo;
  resumen: ResumenRespaldo;
}

// ---------------------------------------------------------------------------
// Rango de fechas que cubre el respaldo
// ---------------------------------------------------------------------------

const CAMPOS_FECHA: Partial<Record<NombreTabla, readonly string[]>> = {
  purchases: ['fecha'],
  plannedItems: ['fechaVenta', 'fechaPrep'],
  productions: ['fechaPrep', 'fechaVenta'],
  sales: ['fecha'],
  inventoryMovements: ['fecha'],
  orders: ['fechaEntrega'],
};

export function calcularRangoFechas(
  datos: MapaTablas,
): { from: FechaLocal; to: FechaLocal } | null {
  let desde: FechaLocal | null = null;
  let hasta: FechaLocal | null = null;

  for (const [tabla, campos] of Object.entries(CAMPOS_FECHA)) {
    const registros = leerTabla(datos, tabla as NombreTabla);
    for (const registro of registros) {
      for (const campo of campos ?? []) {
        const valor = registro[campo];
        if (!esFechaLocal(valor)) continue;
        desde = desde === null ? valor : minima(desde, valor);
        hasta = hasta === null ? valor : maxima(hasta, valor);
      }
    }
  }

  if (desde === null || hasta === null) return null;
  return { from: desde, to: hasta };
}

// ---------------------------------------------------------------------------
// Integridad referencial
// ---------------------------------------------------------------------------

interface ReglaReferencia {
  tabla: NombreTabla;
  campo: string;
  destino: NombreTabla;
  opcional?: boolean;
}

const REGLAS_REFERENCIA: readonly ReglaReferencia[] = [
  { tabla: 'purchaseItems', campo: 'purchaseId', destino: 'purchases' },
  { tabla: 'purchaseItems', campo: 'ingredientId', destino: 'ingredients' },
  { tabla: 'recipeItems', campo: 'recipeId', destino: 'recipes' },
  { tabla: 'recipeItems', campo: 'ingredientId', destino: 'ingredients' },
  { tabla: 'menuTemplateItems', campo: 'templateId', destino: 'menuTemplates' },
  { tabla: 'menuTemplateItems', campo: 'recipeId', destino: 'recipes' },
  { tabla: 'plannedItems', campo: 'recipeId', destino: 'recipes' },
  { tabla: 'productions', campo: 'plannedItemId', destino: 'plannedItems' },
  { tabla: 'productions', campo: 'recipeId', destino: 'recipes' },
  { tabla: 'productionItems', campo: 'productionId', destino: 'productions' },
  { tabla: 'productionItems', campo: 'ingredientId', destino: 'ingredients' },
  { tabla: 'sales', campo: 'recipeId', destino: 'recipes' },
  { tabla: 'sales', campo: 'plannedItemId', destino: 'plannedItems', opcional: true },
  { tabla: 'sales', campo: 'productionId', destino: 'productions', opcional: true },
  { tabla: 'saleDispositions', campo: 'plannedItemId', destino: 'plannedItems' },
  { tabla: 'inventoryMovements', campo: 'ingredientId', destino: 'ingredients' },
  { tabla: 'shoppingChecks', campo: 'ingredientId', destino: 'ingredients' },
  // Un pedido siempre apunta a una receta. El platillo del menu y la venta son
  // enlaces opcionales: se puede pedir algo que todavia no esta en el menu, y
  // en la etapa 1 la venta nunca se crea desde aqui.
  { tabla: 'orders', campo: 'recipeId', destino: 'recipes' },
  { tabla: 'orders', campo: 'plannedItemId', destino: 'plannedItems', opcional: true },
  { tabla: 'orders', campo: 'saleId', destino: 'sales', opcional: true },
];

/** Reporta referencias que apuntan a registros inexistentes (Req. 3.6). */
export function revisarIntegridad(datos: MapaTablas): string[] {
  const problemas: string[] = [];
  const indices = new Map<NombreTabla, Set<string>>();

  for (const tabla of TABLAS) {
    const registros = leerTabla(datos, tabla);
    indices.set(
      tabla,
      new Set(
        registros
          .map((r) => {
            const id = r['id'];
            return id === null || id === undefined ? null : String(id);
          })
          .filter((id): id is string => id !== null),
      ),
    );
  }

  for (const regla of REGLAS_REFERENCIA) {
    const registros = leerTabla(datos, regla.tabla);
    const validos = indices.get(regla.destino) ?? new Set<string>();
    let huerfanos = 0;

    for (const registro of registros) {
      const valor = registro[regla.campo];
      if (valor === null || valor === undefined) {
        if (regla.opcional === true) continue;
        huerfanos++;
        continue;
      }
      if (!validos.has(String(valor))) huerfanos++;
    }

    if (huerfanos > 0) {
      problemas.push(
        `${ETIQUETAS_TABLA[regla.tabla]}: ${huerfanos} ` +
          `${huerfanos === 1 ? 'registro apunta' : 'registros apuntan'} a ` +
          `${ETIQUETAS_TABLA[regla.destino].toLowerCase()} que no existe.`,
      );
    }
  }

  return problemas;
}

// ---------------------------------------------------------------------------
// Serializacion
// ---------------------------------------------------------------------------

/** Con sangria, para que el archivo sea legible si hace falta revisarlo a mano. */
export function serializarRespaldo(archivo: ArchivoRespaldo): string {
  return JSON.stringify(archivo, null, 2);
}

/** `"control-negocio-comida-2026-08-17-1430.respaldo.json"` */
export function nombreArchivoRespaldo(fecha = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  const sello =
    `${fecha.getFullYear()}-${p(fecha.getMonth() + 1)}-${p(fecha.getDate())}` +
    `-${p(fecha.getHours())}${p(fecha.getMinutes())}`;
  return `${IDENTIFICADOR_APP}-${sello}.respaldo.json`;
}

export function tablasVacias(): MapaTablas {
  const vacio = {} as MapaTablas;
  for (const tabla of TABLAS) {
    escribirTabla(vacio, tabla, []);
  }
  return vacio;
}

/**
 * Recorta el mapa a las tablas que el archivo realmente traia.
 *
 * ESTO ES LO QUE PERMITE AGREGAR TABLAS SIN INVALIDAR LOS RESPALDOS VIEJOS.
 *
 * La suma de verificacion se firma sobre el mapa completo al exportar. Si al
 * verificar se recalculara sobre la lista de tablas de la version ACTUAL, un
 * archivo hecho cuando habia 15 tablas se compararia contra un mapa de 16, la
 * cadena a hashear ganaria `"orders":[]` y la suma no coincidiria: todos los
 * respaldos anteriores quedarian marcados como corruptos y con la importacion
 * bloqueada.
 *
 * Firmando solo lo que el archivo trae, un respaldo de 15 tablas se verifica
 * contra 15 y uno de 16 contra 16, y los dos salen validos. Cuando el archivo
 * trae todas las tablas conocidas, el resultado es identico al de antes.
 *
 * Sigue detectando manipulacion real: si a un archivo se le quita una tabla que
 * si estaba cuando se firmo, la suma deja de coincidir.
 */
function soloPresentes(datos: MapaTablas, presentes: readonly NombreTabla[]): MapaTablas {
  const recorte = {} as MapaTablas;
  for (const tabla of presentes) {
    escribirTabla(recorte, tabla, datos[tabla]);
  }
  return recorte;
}

// ---------------------------------------------------------------------------
// Analisis previo a la importacion
// ---------------------------------------------------------------------------

function esObjeto(valor: unknown): valor is Record<string, unknown> {
  return typeof valor === 'object' && valor !== null && !Array.isArray(valor);
}

/**
 * Valida un archivo de respaldo SIN modificar ningun dato (Req. 3.6).
 *
 * Orden de revision: de lo mas barato a lo mas costoso, y de lo que impide
 * continuar a lo que solo advierte.
 *
 *   1. Es JSON valido                                      -> rechazo
 *   2. Es un respaldo de esta aplicacion y formato conocido -> rechazo
 *   3. La version de esquema no es posterior a la soportada -> rechazo
 *   4. Las tablas esperadas son listas                      -> rechazo
 *   5. La suma de verificacion coincide                     -> advertencia
 *   6. La integridad referencial es correcta                -> advertencia
 *
 * Las advertencias no bloquean: un respaldo con algun huerfano sigue siendo
 * mejor que no tener respaldo. Se muestran para decidir con informacion.
 */
export async function analizarRespaldo(texto: string): Promise<RespaldoAnalizado> {
  // 1. JSON valido
  let crudo: unknown;
  try {
    crudo = JSON.parse(texto);
  } catch {
    throw new ErrorRespaldo(
      'El archivo no es un respaldo valido: no se pudo leer su contenido. ' +
        'Verifica que sea el archivo .json que genero esta aplicacion.',
    );
  }

  if (!esObjeto(crudo)) {
    throw new ErrorRespaldo('El archivo no tiene la estructura de un respaldo.');
  }

  // 2. Aplicacion y version de formato
  if (crudo['app'] !== IDENTIFICADOR_APP) {
    throw new ErrorRespaldo(
      'Este archivo no fue generado por esta aplicacion. No se modifico ningun dato.',
    );
  }

  const formatVersion = crudo['formatVersion'];
  if (typeof formatVersion !== 'number' || !Number.isInteger(formatVersion)) {
    throw new ErrorRespaldo('El archivo no indica una version de formato valida.');
  }
  if (formatVersion > FORMATO_VERSION) {
    throw new ErrorRespaldo(
      `El respaldo usa el formato ${formatVersion} y esta aplicacion entiende hasta el ` +
        `${FORMATO_VERSION}. Actualiza la aplicacion antes de importarlo. ` +
        'No se modifico ningun dato.',
    );
  }

  // 3. Una version de esquema posterior se RECHAZA sin tocar nada (Req. 3.11)
  const schemaVersion = crudo['schemaVersion'];
  if (typeof schemaVersion !== 'number' || !Number.isInteger(schemaVersion)) {
    throw new ErrorRespaldo('El archivo no indica una version de esquema valida.');
  }
  if (schemaVersion > VERSION_ESQUEMA) {
    throw new ErrorRespaldo(
      `El respaldo viene de una version mas reciente de la aplicacion ` +
        `(esquema ${schemaVersion} contra ${VERSION_ESQUEMA}). ` +
        'Actualiza la aplicacion antes de importarlo. No se modifico ningun dato.',
    );
  }

  // 4. Tablas esperadas
  const data = crudo['data'];
  if (!esObjeto(data)) {
    throw new ErrorRespaldo('El respaldo no contiene datos.');
  }

  const advertencias: string[] = [];
  const normalizado = tablasVacias();
  /** Tablas conocidas que el archivo SI trae. Necesarias para la suma (paso 5). */
  const presentes: NombreTabla[] = [];

  for (const tabla of TABLAS) {
    const registros = data[tabla];
    if (registros === undefined) {
      advertencias.push(`El respaldo no incluye la tabla "${ETIQUETAS_TABLA[tabla]}".`);
      continue;
    }
    if (!Array.isArray(registros)) {
      throw new ErrorRespaldo(
        `La tabla "${ETIQUETAS_TABLA[tabla]}" del respaldo esta danada: se esperaba una lista.`,
      );
    }
    escribirTabla(normalizado, tabla, registros);
    presentes.push(tabla);
  }

  const desconocidas = Object.keys(data).filter((c) => !TABLAS.includes(c as NombreTabla));
  if (desconocidas.length > 0) {
    advertencias.push(
      'El respaldo trae informacion que esta version no reconoce y se omitira: ' +
        `${desconocidas.join(', ')}.`,
    );
  }

  // 5. Suma de verificacion
  const checksumEsperado = crudo['checksum'];
  const traeChecksum = typeof checksumEsperado === 'string' && checksumEsperado !== '';
  let checksumValido = false;

  if (traeChecksum) {
    const calculado = await calcularChecksum(soloPresentes(normalizado, presentes));
    checksumValido = checksumCoincide(checksumEsperado as string, calculado);
    if (!checksumValido) {
      advertencias.push(
        'La suma de verificacion NO coincide: el archivo se modifico o se corrompio ' +
          'despues de crearse. La importacion queda bloqueada.',
      );
    }
  } else {
    advertencias.push(
      'El respaldo no trae suma de verificacion; no se pudo comprobar su integridad.',
    );
  }

  // 6. Integridad referencial
  advertencias.push(...revisarIntegridad(normalizado));

  const conteos = TABLAS.map((tabla) => ({
    tabla,
    etiqueta: ETIQUETAS_TABLA[tabla],
    registros: normalizado[tabla].length,
  }));

  const rango = calcularRangoFechas(normalizado);

  const archivo: ArchivoRespaldo = {
    app: IDENTIFICADOR_APP,
    formatVersion,
    schemaVersion,
    exportedAt: typeof crudo['exportedAt'] === 'string' ? crudo['exportedAt'] : '',
    device: typeof crudo['device'] === 'string' ? crudo['device'] : 'desconocido',
    counts: esObjeto(crudo['counts']) ? (crudo['counts'] as Record<string, number>) : {},
    dateRange: rango,
    checksum: typeof checksumEsperado === 'string' ? checksumEsperado : '',
    data: normalizado,
  };

  return {
    archivo,
    resumen: {
      formatVersion,
      schemaVersion,
      exportedAt: archivo.exportedAt,
      device: archivo.device,
      conteos,
      totalRegistros: conteos
        .filter((c) => c.tabla !== 'settings')
        .reduce((suma, c) => suma + c.registros, 0),
      rangoFechas: rango === null ? null : { desde: rango.from, hasta: rango.to },
      checksumValido,
      requiereMigracion: schemaVersion < VERSION_ESQUEMA,
      advertencias,
      bloqueaImportacion: traeChecksum && !checksumValido,
    },
  };
}

// ---------------------------------------------------------------------------
// Migracion de respaldos antiguos (Req. 3.10)
// ---------------------------------------------------------------------------

/**
 * Migra los datos de un respaldo desde su version de esquema hasta la actual.
 *
 * Las migraciones se aplican en cadena: un respaldo de la version 1 pasa por
 * 1->2, luego 2->3, y asi. Si falta un eslabon se lanza error ANTES de tocar la
 * base, para no dejarla a medias.
 */
export function migrarDatos(datos: MapaTablas, desdeVersion: number): MapaTablas {
  const migraciones: Record<number, (d: MapaTablas) => MapaTablas> = {
    /**
     * 1 -> 2: se agrego la tabla de pedidos.
     *
     * No hay nada que transformar: ningun registro existente cambia de forma.
     * `analizarRespaldo` ya dejo `orders` como lista vacia al normalizar, asi
     * que basta devolver los datos tal cual.
     *
     * Aun asi la entrada TIENE que existir: sin ella esta funcion lanzaria
     * error y ningun respaldo hecho con la version 1 se podria importar.
     */
    1: (d) => d,

    /**
     * 2 -> 3: se agrego la tabla de movimientos de caja, y `fechaPago` a los pedidos.
     *
     * Tampoco hay nada que transformar. `analizarRespaldo` deja `cashMovements` como
     * lista vacia al normalizar, y `fechaPago` ausente se lee igual que null: un
     * pedido viejo marcado como pagado no dice en que dia se cobro, y eso es la
     * verdad, no un dato faltante que haya que inventar.
     *
     * La entrada TIENE que existir aunque no haga nada: sin ella ningun respaldo
     * hecho con la version 2 se podria importar.
     */
    2: (d) => d,
  };

  let actuales = datos;
  let version = desdeVersion;

  while (version < VERSION_ESQUEMA) {
    const migracion = migraciones[version];
    if (migracion === undefined) {
      throw new ErrorRespaldo(
        `No existe una migracion del esquema ${version} al ${version + 1}. ` +
          'No se modifico ningun dato.',
      );
    }
    actuales = migracion(actuales);
    version++;
  }

  return actuales;
}
